import type {ReactElement} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {addDuplicatedPage, addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {getIsPosterInitialized, getNumberOfPages, isPageVideo} from '@Components/poster-editor/poster-editor-reducer';
import {useAppSelector} from '@/hooks';
import {onDeletePage, onMoveDownPage, onMoveUpPage} from '@Components/poster-editor/library/poster-editor-library';
import styles from './page-control-bar.module.scss';
import {PagePlayPauseButton} from '@Components/poster-editor/components/page-play-pause-button/page-play-pause-button';
import {AddPageGA4EventParams, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useIsSinglePageLayout} from '@Hooks/poster-editor/useIsSinglePageLayout';

interface PageControlBarProps {
  pageId: string;
}

export function PageControlBar({...props}: PageControlBarProps): ReactElement | null {
  const pagesLength = useAppSelector(getNumberOfPages);
  const isVideoPage = useAppSelector((state) => isPageVideo(state, props.pageId));
  const isSinglePageLayout = useIsSinglePageLayout();
  const isPosterInitialized = useAppSelector(getIsPosterInitialized);
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const pageOrder = useAppSelector((state) => {
    const pageIndex = state.posterEditor.posterObject?.pages?.pageOrder.indexOf(props.pageId);
    return pageIndex !== -1 ? pageIndex : undefined;
  });
  const isMultiPageEnabled = window.PMW.isMultipageAvailable;

  if (isSinglePageLayout || pageOrder === undefined || isVersionHistoryModeActive) {
    return null;
  }

  const getPageEditingOptions = (): ReactElement => {
    const isPageLimitReached = pagesLength >= window.PMW.MAX_NUMBER_OF_PAGES;
    return (
      <>
        {isVideoPage ? <PagePlayPauseButton pageId={props.pageId} /> : null}
        {isMultiPageEnabled ? (
          <IconWithTooltip
            icon="icon-new-page"
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            isDisabled={isPageLimitReached || !isPosterInitialized}
            onClick={onAddPage}
            tooltipText={window.i18next.t('pmwjs_add_page')}
          />
        ) : null}
        {isMultiPageEnabled ? (
          <IconWithTooltip
            icon="icon-copy"
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            isDisabled={isPageLimitReached || !isPosterInitialized}
            onClick={onDuplicatePage}
            tooltipText={window.i18next.t('pmwjs_duplicate_page')}
          />
        ) : null}
        {pageOrder === 0 ? null : (
          <IconWithTooltip
            icon="icon-arrow-top"
            isDisabled={pageOrder === 0 || !isPosterInitialized}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onMoveUpPage(props.pageId);
            }}
            tooltipText={window.i18next.t('pmwjs_move_up')}
          />
        )}
        {pageOrder === pagesLength - 1 ? null : (
          <IconWithTooltip
            icon="icon-arrow-bottom"
            isDisabled={pageOrder === pagesLength - 1 || !isPosterInitialized}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onMoveDownPage(props.pageId);
            }}
            tooltipText={window.i18next.t('pmwjs_move_down')}
          />
        )}
        {isMultiPageEnabled ? (
          <IconWithTooltip
            icon="icon-delete"
            className="color-danger-100"
            isDisabled={pagesLength === 1 || !isPosterInitialized}
            size={IconSize.SIZE_ICON_16}
            type={IconType.GHOST_UNCOLORED}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              onDeletePage(props.pageId);
            }}
            tooltipText={window.i18next.t('pmwjs_delete_page')}
          />
        ) : null}
      </>
    );
  };

  const onDuplicatePage = (): void => {
    const page = window.posterEditor?.whiteboard?.pages.pagesHashMap[props.pageId];
    if (!page) {
      return;
    }

    addDuplicatedPage(page).catch((e: unknown) => {
      console.error(e);
    });
  };

  const onAddPage = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.ADD_PAGE, {
      [GA4EventParamName.TYPE]: AddPageGA4EventParams.PAGE_RIGHT_OPTIONS,
    });
    if (pageOrder !== undefined) {
      addNewPage(pageOrder + 1).catch((e: unknown) => {
        console.error(e);
      });
    }
  };

  return <div className={`flex-v-row spacing-p-l-1 ${styles.container}`}>{getPageEditingOptions()}</div>;
}
